// src/stores/driverDonHangStore.js
import { defineStore } from "pinia";
import { DonHangApi } from "@/services/donHangService";

export const useDriverDonHangStore = defineStore("driverDonHang", {
  state: () => ({
    // --- Hai tab chính ---
    isLoadingPool: false, // Hàng Đợi (Pool)
    isLoadingMyList: false, // Đơn Của Tôi (active)
    listError: null,
    poolOrders: [], // Page.content từ /api/don-hang/pool
    myOrders: [], // Mảng từ /api/don-hang/cua-toi/active
    completedOrders: [], // danh sách đơn đã hoàn thành

    // --- Modal (Chi tiết / Claim) ---
    isLoadingDetail: false,
    detailError: null,
    currentOrder: null,
  }),

  getters: {
    poolCount: (s) => s.poolOrders.length,
    myOrdersCount: (s) => s.myOrders.length,
  },

  actions: {
    // ========== POOL ==========

    /**
     * Lấy danh sách Hàng Đợi (Pool) – trả về Page<DonHangResponse>
     * -> phải lấy res.content
     */

    async fetchCompletedOrders() {
      this.loading = true;
      this.error = null;

      try {
        // gọi API "đơn của tôi"
        // giả định backend hỗ trợ filter trạng thái qua query param
        // ví dụ: trangThai=HOAN_THANH
        const data = await DonHangApi.getMyOrdersList({
          trangThai: "HOAN_THANH",
          page: 0,
          size: 20,
        });

        // trường hợp backend trả về dạng page {content, totalElements,...}
        if (data && Array.isArray(data.content)) {
          this.completedOrders = data.content;
        }
        // trường hợp backend trả thẳng array
        else if (Array.isArray(data)) {
          this.completedOrders = data;
        }
        // fallback an toàn
        else {
          this.completedOrders = [];
        }
      } catch (err) {
        console.error("fetchCompletedOrders error", err);
        this.error = err?.message || "Lỗi tải danh sách";
        this.completedOrders = [];
      } finally {
        this.loading = false;
      }
    },

    async fetchPool(params = { page: 0, size: 50 }) {
      this.isLoadingPool = true;
      this.listError = null;
      try {
        const res = await DonHangApi.listPool(params);
        const data = res?.content; // <-- BẮT BUỘC: Page.content
        this.poolOrders = Array.isArray(data) ? data : [];
      } catch (e) {
        this.listError =
          e?.response?.data?.message || e?.message || "Lỗi tải Hàng Đợi";
        this.poolOrders = [];
      } finally {
        this.isLoadingPool = false;
      }
    },

    // ========== MY ACTIVE ORDERS ==========
    /**
     * Lấy danh sách Đơn Của Tôi (active, không phân trang) – trả về mảng
     * -> KHÔNG dùng .content
     */

    async fetchMyOrders() {
      this.isLoadingMyList = true;
      this.listError = null;
      try {
        const res = await DonHangApi.getMyActiveOrders();
        // API này trả mảng DonHangResponse[]
        this.myOrders = Array.isArray(res) ? res : [];
      } catch (e) {
        this.listError =
          e?.response?.data?.message || e?.message || "Lỗi tải Đơn Của Tôi";
        this.myOrders = [];
      } finally {
        this.isLoadingMyList = false;
      }
    },

    /**
     * Tải đồng thời cả Pool và MyOrders (dùng khi vào Dashboard)
     */
    async fetchAll() {
      await Promise.all([this.fetchPool(), this.fetchMyOrders()]);
    },

    // ========== MODAL ==========
    async openDetailModal(orderId) {
      this.isLoadingDetail = true;
      this.detailError = null;
      this.currentOrder = null;
      try {
        this.currentOrder = await DonHangApi.getById(orderId);
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Lỗi tải chi tiết";
      } finally {
        this.isLoadingDetail = false;
      }
    },

    async openClaimModal(order) {
      // Modal Claim dùng dữ liệu tóm tắt ngay từ danh sách Pool
      this.isLoadingDetail = false;
      this.detailError = null;
      this.currentOrder = order || null;
    },

    clearModal() {
      this.currentOrder = null;
      this.detailError = null;
    },

    // ========== CLAIM ==========
    /**
     * Tài xế nhận đơn (Claim) từ Pool
     */
    async claimOrder(orderId) {
      if (!orderId) return;
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        const res = await DonHangApi.driverClaimDonHang(orderId);
        const msg =
          res?.message || res?.data?.message || "Nhận đơn thành công!";
        alert(msg);

        // Đồng bộ 2 list
        await this.fetchAll();

        // (Tuỳ chọn) UX mượt hơn khi chưa fetch xong:
        // this.poolOrders = this.poolOrders.filter(o => o.id !== orderId);
      } catch (e) {
        const apiMsg =
          e?.response?.data?.message ||
          e?.message ||
          "Lỗi nhận đơn (Có thể tài xế khác đã nhận)";
        this.detailError = apiMsg;
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    // ========== CHECKPOINT ==========
    /**
     * Điểm dừng: 'den' hoặc 'hoan-thanh'
     */
    async runStopCheckpoint(actionType, diemDungId, successMessage) {
      if (!diemDungId || this.isLoadingDetail) return;
      this.isLoadingDetail = true;
      this.detailError = null;

      try {
        let res;
        if (actionType === "den") {
          res = await DonHangApi.driverDenDiemDung(diemDungId);
        } else if (actionType === "hoan-thanh") {
          res = await DonHangApi.driverHoanThanhDiemDung(diemDungId);
        } else {
          throw new Error("Hành động không hợp lệ");
        }

        // Chuẩn hoá để lấy đơn hàng đã cập nhật
        const updatedOrder = res?.donHang || res?.data || res;

        if (!updatedOrder?.id && this.currentOrder?.id) {
          // fallback lấy detail nếu BE không trả full
          this.currentOrder = await DonHangApi.getById(this.currentOrder.id);
        } else {
          this.currentOrder = updatedOrder;
        }

        alert(successMessage || "Cập nhật thành công!");

        // Cập nhật lại myOrders nếu đang hiển thị
        if (this.currentOrder?.id && Array.isArray(this.myOrders)) {
          const idx = this.myOrders.findIndex(
            (o) => o.id === this.currentOrder.id
          );
          if (idx !== -1) this.myOrders.splice(idx, 1, this.currentOrder);
        }

        // Nếu đơn hoàn thành -> refetch myOrders (đơn có thể biến mất khỏi active)
        if (this.currentOrder?.trangThai === "HOAN_THANH") {
          await this.fetchMyOrders();
        }
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Thao tác thất bại";
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    async driverXacMinhGiaoHang(stopId, taiXeId, imageUrl) {
      this.isLoadingDetail = true;
      this.detailError = null;

      try {
        console.log("[driverDonHangStore] gọi API xác minh giao hàng", {
          stopId,
          taiXeId,
          imageUrl,
        });

        const res = await DonHangApi.driverXacMinhGiaoHang(
          stopId,
          taiXeId,
          imageUrl
        );

        this.currentOrder = res.data;
        console.log("[driverDonHangStore] cập nhật currentOrder thành công");
      } catch (err) {
        console.error("driverXacMinhGiaoHang error:", err);
        this.detailError =
          err?.response?.data?.message || "Lỗi xác minh giao hàng.";
        throw err;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    // ========== FAILURE ==========
    /**
     * Báo thất bại toàn đơn
     */
    async reportFailure(orderId, lyDo) {
      if (!orderId || !lyDo || this.isLoadingDetail) return;
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        const res = await DonHangApi.driverThatBai(orderId, lyDo);
        const msg =
          res?.message || res?.data?.message || "Báo cáo thất bại thành công.";
        alert(msg);

        // Đồng bộ cả 2 list (đơn thất bại sẽ rời active; Pool có thể không đổi)
        await this.fetchAll();

        // Nếu BE trả đơn đã cập nhật thì đồng bộ modal
        const updatedOrder = res?.donHang || res?.data || null;
        if (updatedOrder?.id) {
          this.currentOrder = updatedOrder;
        }
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Lỗi báo cáo thất bại";
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },
  },
});
